import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { apiGet } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { useScope } from "../scope/ScopeContext";
import { scopeLabel } from "../scope/focus_rules";
import { ChangePasswordDialog } from "../components/ChangePasswordDialog";

type IconName = "star" | "send" | "door" | "grid" | "calendar" | "plug" | "menu" | "close" | "map";

type NavItem = {
  to: string;
  label: string;
  description: string;
  icon: IconName;
  end?: boolean;
  children?: { to: string; label: string; badge?: "pending" }[];
};

/** 16px stroke icons, inline so the client stays dependency-free. */
const ICON_PATHS: Record<IconName, string> = {
  star: "M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4l-5.9 3.1 1.2-6.5L2.5 9.4l6.6-.9z",
  send: "M22 2L11 13M22 2l-7 20-4-9-9-4z",
  door: "M13 3H5v18h8M13 3l6 2v14l-6 2M13 3v18M10 12h.01",
  grid: "M3 3h8v8H3zM13 3h8v8h-8zM3 13h8v8H3zM13 13h8v8h-8z",
  calendar: "M3 5h18v16H3zM3 10h18M8 3v4M16 3v4",
  plug: "M9 2v6M15 2v6M5 8h14l-1 5a6 6 0 0 1-12 0zM12 19v3",
  menu: "M4 7h16M4 12h16M4 17h16",
  close: "M6 6l12 12M18 6L6 18",
  map: "M9 3l6 2 6-2v18l-6 2-6-2-6 2V5zM9 3v18M15 5v18",
};

function Icon({ name, className }: { name: IconName; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      <path d={ICON_PATHS[name]} />
    </svg>
  );
}

/**
 * Every row is a link. Nothing in the sidebar is a heading.
 *
 * The grouped version (headings "Reviews" / "Outreach" / "Ups" / "Posts" /
 * "Setup" over the links) read as buttons that did nothing — Kat's words:
 * "it looks like I should click it, and I'm not supposed to" (2026-09-12).
 * So the grouping now lives in nesting instead: Outreach is one link to its
 * overview with its four sub-pages indented beneath it, and everything else
 * is a single top-level link with the old section note as its description.
 *
 * Ups stays its own row rather than an outreach child on purpose — it asks a
 * different population a different question and never asks for a review. See
 * pages/UpsPage.tsx.
 */
const navItems: NavItem[] = [
  { to: "/reviews", label: "Review queue", description: "Reply to Google reviews", icon: "star" },
  {
    to: "/outreach",
    label: "Outreach",
    description: "Sends, responses, ratings",
    icon: "send",
    end: true,
    children: [
      { to: "/outreach/queue", label: "Approval queue", badge: "pending" },
      { to: "/outreach/feedback", label: "Manager feedback" },
      { to: "/outreach/recognition", label: "Team recognition" },
      { to: "/outreach/settings", label: "Settings" },
    ],
  },
  { to: "/ups", label: "Ups", description: "Showroom visits that didn't buy", icon: "door" },
  { to: "/templates", label: "Templates", description: "Post library", icon: "grid" },
  { to: "/calendar", label: "Calendar", description: "Scheduled posts", icon: "calendar" },
  { to: "/integrations", label: "Integrations", description: "Google Business + Facebook", icon: "plug" },
  { to: "/admin/osm", label: "OSM / Mapbox", description: "Rooftop listings", icon: "map" },
];

const ROLE_LABEL: Record<string, string> = {
  super_admin: "Super admin",
  group_admin: "Group admin",
  store_admin: "Store admin",
  user: "User",
};

const SCOPE_CHIP: Record<string, string> = {
  super_admin: "All stores",
  group_admin: "Group",
  store_admin: "One store",
  user: "One store",
};

export function AppLayout() {
  const { user, logout } = useAuth();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const {
    dealers,
    groups,
    focusedGroupId,
    focusedDealerId,
    setFocusedGroupId,
    setFocusedDealerId,
    dealersInFocus,
  } = useScope();

  const showGroupPicker = user?.role === "super_admin" && groups.length > 0;
  const showStorePicker = dealersInFocus.length > 1;
  const focusedDealer = focusedDealerId
    ? dealers.find((d) => d.id === focusedDealerId) ?? null
    : null;
  const focusedGroup = focusedGroupId
    ? groups.find((g) => g.id === focusedGroupId) ?? null
    : null;

  // What the phone top bar says the view is scoped to. On desktop the sidebar
  // picker is always on screen; below lg it is inside a closed drawer, so
  // without this a stored group focus hid most of the fleet with no visible
  // cause. `narrowed` is what makes a filtered view look filtered.
  const scope = scopeLabel({
    groupName: focusedGroup?.name ?? null,
    dealerName: focusedDealer?.name ?? null,
    dealerCount: dealersInFocus.length,
  });

  const onboardingLinkVisible = user?.role === "super_admin";

  // People waiting on a human, shown beside "Approval queue" so the work is
  // visible from any page. Re-read on every navigation and when the focused
  // store changes; the queue page itself is the source of truth once opened.
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState<number | null>(null);

  // Below lg the sidebar is a drawer. It closes itself on every navigation so
  // tapping a link never leaves the menu covering the page it just opened.
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const params = new URLSearchParams({ status: "pending", limit: "1" });
    if (focusedDealerId) params.set("dealerId", focusedDealerId);
    apiGet<{ statusCounts?: Record<string, number> }>(`/api/outreach/recipients?${params}`)
      .then((res) => {
        if (!cancelled) setPendingCount(res.statusCounts?.pending ?? 0);
      })
      .catch(() => {
        if (!cancelled) setPendingCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [user, focusedDealerId, location.pathname]);

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-slate-50 lg:flex-row">
      {/* Phone / tablet top bar. The sidebar below is a drawer at these widths. */}
      <header className="flex h-14 shrink-0 items-center gap-3 bg-ink-950 px-3 text-white lg:hidden">
        <button
          type="button"
          onClick={() => setNavOpen(true)}
          aria-label="Open menu"
          aria-expanded={navOpen}
          className="flex h-10 w-10 items-center justify-center rounded-lg text-ink-200 hover:bg-ink-800 hover:text-white"
        >
          <Icon name="menu" className="h-5 w-5" />
        </button>
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-brand-500 text-white">
          <Icon name="star" className="h-3.5 w-3.5" />
        </span>
        {/* The wordmark yields to the scope chip on a narrow phone. You always
            know which app you opened; you do NOT know what it is filtered to,
            and below lg the picker is behind a closed drawer. */}
        <span className="hidden text-sm font-bold tracking-tight sm:inline">
          Savvy Reputation
        </span>
        {(showGroupPicker || showStorePicker) && (
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            title={
              scope.narrowed
                ? `Showing ${scope.text} only — tap to change`
                : "Tap to filter by group or store"
            }
            className={[
              "flex min-w-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition",
              scope.narrowed
                ? "bg-brand-500 text-white hover:bg-brand-400"
                : "bg-ink-800 text-ink-200 hover:bg-ink-700",
            ].join(" ")}
          >
            <span className="truncate">{scope.text}</span>
            <span aria-hidden className="shrink-0 opacity-70">
              ▾
            </span>
          </button>
        )}
        {(pendingCount ?? 0) > 0 && (
          <NavLink
            to="/outreach/queue"
            className="ml-auto rounded-full bg-brand-500 px-2 py-0.5 text-xs font-bold tabular-nums text-white"
            title={`${pendingCount} waiting for approval`}
          >
            {pendingCount}
          </NavLink>
        )}
      </header>

      {navOpen && (
        <div
          className="fixed inset-0 z-30 bg-ink-950/60 lg:hidden"
          onClick={() => setNavOpen(false)}
          aria-hidden
        />
      )}

      <aside
        className={[
          "fixed inset-y-0 left-0 z-40 flex w-72 max-w-[85vw] flex-col bg-ink-950 text-ink-200 shadow-2xl transition-transform duration-200",
          "lg:static lg:z-auto lg:w-64 lg:max-w-none lg:translate-x-0 lg:shadow-none",
          navOpen ? "translate-x-0" : "-translate-x-full",
        ].join(" ")}
      >
        <div className="flex h-14 items-center gap-2.5 border-b border-ink-800 px-5">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-brand-500 text-white">
            <Icon name="star" className="h-3.5 w-3.5" />
          </span>
          <span className="text-sm font-bold tracking-tight text-white">Savvy Reputation</span>
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            aria-label="Close menu"
            className="ml-auto flex h-9 w-9 items-center justify-center rounded-lg text-ink-300 hover:bg-ink-800 hover:text-white lg:hidden"
          >
            <Icon name="close" className="h-4 w-4" />
          </button>
        </div>

        {(showGroupPicker || showStorePicker) && (
          <div className="space-y-3 border-b border-ink-800 px-4 py-3">
            {showGroupPicker && (
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-ink-300">
                  Group
                </label>
                <select
                  value={focusedGroupId ?? ""}
                  onChange={(e) => setFocusedGroupId(e.target.value || null)}
                  className="mt-1 block w-full rounded-md border border-ink-700 bg-ink-800 px-2 py-1.5 text-xs text-white focus:border-brand-500 focus:outline-none"
                >
                  <option value="">All groups</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name} ({g.dealerCount})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {showStorePicker && (
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-ink-300">
                  Store
                </label>
                <select
                  value={focusedDealerId ?? ""}
                  onChange={(e) => setFocusedDealerId(e.target.value || null)}
                  className="mt-1 block w-full rounded-md border border-ink-700 bg-ink-800 px-2 py-1.5 text-xs text-white focus:border-brand-500 focus:outline-none"
                >
                  <option value="">
                    {focusedGroup
                      ? `All ${focusedGroup.name} stores`
                      : "All visible stores"}
                  </option>
                  {dealersInFocus.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
                {focusedDealer && !focusedDealer.autoPublishEnabled && (
                  <div className="mt-2 text-[11px] text-rose-300">
                    Auto-publish disabled for this store.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {navItems.map((item) => (
            <div key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  [
                    "flex items-start gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors lg:py-2",
                    isActive
                      ? "bg-brand-600 text-white shadow-sm"
                      : "text-ink-200 hover:bg-ink-800 hover:text-white",
                  ].join(" ")
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      name={item.icon}
                      className={["mt-0.5 shrink-0", isActive ? "text-white" : "text-ink-300"].join(" ")}
                    />
                    <span className="flex flex-col">
                      <span className="font-semibold">{item.label}</span>
                      <span className={["text-xs", isActive ? "text-white/80" : "text-ink-300"].join(" ")}>
                        {item.description}
                      </span>
                    </span>
                  </>
                )}
              </NavLink>
              {item.children && (
                <div className="ml-6 mt-0.5 space-y-0.5 border-l border-ink-700 pl-3">
                  {item.children.map((child) => (
                    <NavLink
                      key={child.to}
                      to={child.to}
                      className={({ isActive }) =>
                        [
                          "flex items-center justify-between rounded-md px-2 py-1.5 text-xs transition-colors lg:py-1",
                          isActive
                            ? "bg-ink-800 font-semibold text-brand-200"
                            : "text-ink-300 hover:bg-ink-800 hover:text-white",
                        ].join(" ")
                      }
                    >
                      <span>{child.label}</span>
                      {child.badge === "pending" && (pendingCount ?? 0) > 0 && (
                        <span
                          className="ml-2 rounded-full bg-brand-500 px-1.5 py-px text-[10px] font-bold tabular-nums text-white"
                          title={`${pendingCount} waiting for approval`}
                        >
                          {pendingCount}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
          {onboardingLinkVisible && (
            <NavLink
              to="/onboarding/gbp"
              className={({ isActive }) =>
                [
                  "mt-4 flex items-center gap-2 rounded-lg px-3 py-2 text-xs transition-colors",
                  isActive
                    ? "bg-ink-800 text-brand-200"
                    : "text-ink-300 hover:bg-ink-800 hover:text-white",
                ].join(" ")
              }
            >
              <span aria-hidden>+</span>
              <span>Onboard a dealer group</span>
            </NavLink>
          )}
        </nav>

        <div className="border-t border-ink-800 p-3">
          <div className="px-2 text-xs text-ink-300">Signed in as</div>
          <div className="px-2 text-sm font-semibold text-white truncate">
            {user?.fullName ?? user?.email}
          </div>
          <div className="mt-1 flex items-center gap-2 px-2">
            <span className="text-xs text-ink-300">{user ? ROLE_LABEL[user.role] : ""}</span>
            <span className="rounded-full bg-ink-800 px-1.5 py-0.5 text-[10px] font-medium text-ink-200 ring-1 ring-inset ring-ink-700">
              {user ? SCOPE_CHIP[user.role] : ""}
            </span>
          </div>
          <button
            onClick={() => setShowChangePassword(true)}
            className="mt-2 w-full rounded-md px-2 py-1.5 text-left text-xs text-ink-300 hover:bg-ink-800 hover:text-white"
          >
            Change password
          </button>
          <button
            onClick={() => void logout()}
            className="mt-0.5 w-full rounded-md px-2 py-1.5 text-left text-xs text-ink-300 hover:bg-ink-800 hover:text-white"
          >
            Sign out
          </button>
        </div>
      </aside>
      <main className="min-h-0 flex-1 overflow-y-auto">
        <Outlet />
      </main>
      {showChangePassword && (
        <ChangePasswordDialog onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
}
